import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiInstance } from "@shared/services";
import {
  convertObjectToCamelCase,
  convertObjectToSnakeCase,
} from "@shared/helpers";

import { PAYFAC_QUERIES } from "./queries";

import type {
  IPendency,
  IPendencyRateError,
  IPendencyWithPagination,
} from "@payfac/types";

const URLS = {
  pendencies: `/v2/payfac/pendencies`,
};

const api = apiInstance({
  enableBearerToken: true,
  enableXTokenApi: true,
});

export const useGetPendenciesList = ({
  filters,
}: {
  filters?: {
    limit?: number;
    page?: number;
    timestamp?: string;
    webapp?: boolean;
  };
} = {}) => {
  const { t } = useTranslation();

  const getPendenciesList = async (): Promise<{
    success: boolean;
    message: string;
    data?: IPendencyWithPagination;
  }> => {
    try {
      const { data } = await api.get(URLS.pendencies, {
        params: filters,
      });

      return {
        success: true,
        message: t("payfac.pendenciesServiceListSuccess"),
        data: convertObjectToCamelCase(data) as IPendencyWithPagination,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("payfac.pendenciesServiceListError"),
      };
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.PENDENCIES, filters],
    queryFn: () => getPendenciesList(),
  });
};

export const useGetPendencyDetails = (id?: number | string) => {
  const { t } = useTranslation();

  const getPendencyDetails = async (): Promise<{
    success: boolean;
    message: string;
    data?: IPendency;
  }> => {
    try {
      const { data } = await api.get(`${URLS.pendencies}/${id}`);

      return {
        success: true,
        message: t("payfac.pendenciesServiceShowSuccess"),
        data: convertObjectToCamelCase(data) as IPendency,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("payfac.pendenciesServiceShowError"),
      };
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.PENDENCIES, id],
    queryFn: () => getPendencyDetails(),
    enabled: !!id,
  });
};

export const usePatchPendency = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const patchPendency = async ({
    id,
    errors,
  }: {
    id: number | string;
    errors: IPendencyRateError[];
  }): Promise<{ success: boolean; message: string; data?: any }> => {
    try {
      const { data } = await api.patch(
        `${URLS.pendencies}/${id}`,
        convertObjectToSnakeCase({ context: { errors } }),
      );

      return {
        success: true,
        message: t("payfac.pendenciesServicePatchSuccess"),
        data: convertObjectToCamelCase(data),
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("payfac.pendenciesServicePatchError"),
      };
    }
  };

  return useMutation({
    mutationFn: patchPendency,
    onSuccess: (response, variables) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.PENDENCIES, variables.id],
        });
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.PENDENCIES],
        });
      }
    },
  });
};
